import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import { Id } from './id'
import type { Identifiable } from './identifiable'

/**
 * There are several different types of models that make up a DMS schema, each
 * with its own purpose. This enum identifies each of those types. However,
 * each model type also has its own interface. Look at those interfaces to
 * learn more about each model type.
 */
export enum Type {
  Attribute = 'attr',
  Dimension = 'dim',
  Entity = 'ent',
  Enum = 'enum',
  Purpose = 'purp',
  Snapshot = 'snap',
  Structure = 'struct',
}
// Needs to be before namespace is defined
const TYPES: ReadonlySet<Type> = new Set(Object.values(Type) as Type[])
export namespace Type {
  export const ALL: readonly Type[] = Array.from(TYPES)
  export const isType = TypeChecker.Factory.oneOf(TYPES).usingAlias('Type')
  export const assertIsType: TypeAsserter<Type> =
    TypeAsserter.Factory.from(isType)

  export function from<TType extends Type>(
    identifiable: Identifiable<TType>,
  ): TType
  export function from(identifiable: Identifiable): Type
  // NOTE: requires `any` to be compatible with all overloads
  export function from(identifiable: Identifiable<any>): Type {
    // NOTE: requires casting to overcome the `any` in the implementation signature
    const id = Id.from(identifiable) satisfies Id<any> as Id
    const [type] = Id.split(id)
    assertIsType(type)
    return type
  }
}
